import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, ChevronRight, Minus, Plus, Truck, Store, Sprout, Droplets, Sun, Calculator, Tag, Hammer } from "lucide-react";
import { useCart } from "@/context/CartContext";
import FavoriteButton from "@/components/FavoriteButton";
import Seo from "@/components/Seo";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

const CareItem = ({ icon: Icon, label, value }) => value ? (
  <div className="flex items-start gap-3 rounded-xl bg-white border border-border p-4">
    <Icon size={20} className="text-brand shrink-0 mt-0.5" />
    <div><p className="text-xs font-bold uppercase tracking-wider text-stone">{label}</p><p className="text-sm text-forest mt-0.5">{value}</p></div>
  </div>
) : null;

function ServiceQuote({ p }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const isVeg = p.product_type === "vegetal";
  const serviceType = isVeg ? "Plantation par un paysagiste" : "Pose / mise en œuvre";
  const input = "w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand/30";

  const submit = async () => {
    if (!f.name || !f.email) { toast.error("Nom et email requis"); return; }
    setLoading(true);
    try {
      await api.post("/shop/product-service-quote", {
        product_name: p.name, product_slug: p.slug, service_type: serviceType, ...f,
      });
      setSent(true);
      toast.success("Demande envoyée ! Un paysagiste vous recontacte rapidement.");
    } catch { toast.error("Erreur, réessayez."); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-6 rounded-2xl border border-brand/30 bg-brand/5 p-5" data-testid="service-quote">
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} data-testid="service-quote-check"
          className="mt-1 h-5 w-5 rounded border-border text-brand accent-[#2E9E2E] cursor-pointer" />
        <span className="text-sm text-forest">
          <strong className="flex items-center gap-1.5"><Hammer size={16} className="text-brand" /> Je souhaite un devis pour la {isVeg ? "plantation" : "pose"} par un de nos paysagistes</strong>
          <span className="text-stone">Confiez la {isVeg ? "plantation et l'installation" : "mise en œuvre"} à nos experts. Devis gratuit et sans engagement.</span>
        </span>
      </label>

      {open && !sent && (
        <div className="mt-4 space-y-3" data-testid="service-quote-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={input} placeholder="Nom complet *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="sq-name" />
            <input className={input} placeholder="Téléphone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="sq-phone" />
          </div>
          <input className={input} type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="sq-email" />
          <textarea className={input} rows={2} placeholder="Précisez votre besoin (surface, lieu, délai…)" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="sq-message" />
          <button onClick={submit} disabled={loading} data-testid="sq-submit"
            className="w-full rounded-full bg-brand text-cream py-3 font-semibold hover:bg-forest transition-colors disabled:opacity-50">
            {loading ? "Envoi…" : "Demander mon devis gratuit"}
          </button>
        </div>
      )}
      {sent && <p className="mt-4 text-sm font-medium text-green-700" data-testid="service-quote-sent">✓ Merci ! Votre demande de devis a bien été transmise à notre équipe.</p>}
    </div>
  );
}

function VolumeCalc({ p, add }) {
  const [d, setD] = useState({ l: "", w: "", h: "" });
  const volume = (Number(d.l) || 0) * (Number(d.w) || 0) * ((Number(d.h) || 0) / 100);
  const perUnit = p.sold_by === "bigbag" ? (p.bigbag_volume_m3 || 1) : 1;
  const qty = volume > 0 ? Math.max(1, Math.ceil(volume / perUnit)) : 0;
  const unitPrice = p.group_price != null ? p.group_price : (p.effective_price ?? p.price);
  const total = qty * unitPrice;
  const weightKg = (Number(p.density) || 0) > 0 ? volume * Number(p.density) * 1000 : 0;
  const weightLabel = weightKg >= 1000 ? `${(weightKg / 1000).toFixed(2)} t` : `${Math.max(1, Math.round(weightKg))} kg`;
  const input = "w-full rounded-lg border border-border px-3 py-2 text-sm text-center";
  return (
    <div className="mt-6 rounded-2xl bg-sage/50 border border-border p-5" data-testid="volume-calc">
      <p className="font-semibold text-forest flex items-center gap-2 mb-3"><Calculator size={18} className="text-brand" /> Calculez votre volume</p>
      <p className="text-xs text-stone mb-3">Prix annoncé : <strong>{unitPrice.toFixed(2)} € / {p.unit_label}</strong></p>
      <div className="grid grid-cols-3 gap-3">
        <div><label className="text-xs text-stone">Longueur (m)</label><input className={input} type="number" value={d.l} onChange={(e) => setD({ ...d, l: e.target.value })} data-testid="vol-l" /></div>
        <div><label className="text-xs text-stone">Largeur (m)</label><input className={input} type="number" value={d.w} onChange={(e) => setD({ ...d, w: e.target.value })} data-testid="vol-w" /></div>
        <div><label className="text-xs text-stone">Épaisseur (cm)</label><input className={input} type="number" value={d.h} onChange={(e) => setD({ ...d, h: e.target.value })} data-testid="vol-h" /></div>
      </div>
      {volume > 0 && (
        <div className="mt-4 space-y-3">
          <div className="rounded-xl bg-cream border border-border p-4 space-y-1.5" data-testid="vol-result">
            <p className="text-sm text-forest flex justify-between">Volume calculé <strong>{volume.toFixed(2)} m³</strong></p>
            <p className="text-sm text-forest flex justify-between">Quantité nécessaire <strong>{p.sold_by === "m3" ? `${qty} m³` : `${qty} × ${p.unit_label}`}</strong></p>
            {weightKg > 0 && <p className="text-sm text-forest flex justify-between" data-testid="vol-weight">Poids estimé (densité {Number(p.density)}) <strong>{weightLabel}</strong></p>}
            <div className="border-t border-border pt-1.5 flex justify-between items-baseline">
              <span className="text-sm text-forest">Prix total estimé</span>
              <span className="font-display text-2xl text-brand" data-testid="vol-total">{total.toFixed(2)} €</span>
            </div>
          </div>
          <button onClick={() => { add(p, qty, { volume: true }); toast.success(`Ajouté : ${p.sold_by === "m3" ? `${qty} m³` : `${qty} × ${p.unit_label}`}`); }} data-testid="vol-add"
            className="w-full rounded-full bg-brand text-cream px-5 py-2.5 text-sm font-semibold hover:bg-forest">Ajouter {p.sold_by === "m3" ? `${qty} m³` : `${qty} × ${p.unit_label}`} — {total.toFixed(2)} €</button>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => { window.scrollTo(0, 0); api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => {}); }, [slug]);
  if (!p) return <div className="pt-40 pb-40 text-center text-stone">Chargement…</div>;

  const isVeg = p.product_type === "vegetal";
  const isMat = p.product_type === "materiau";

  return (
    <div className="bg-cream min-h-screen">
      <Seo title={p.name} path={`/boutique/${p.slug}`} description={p.description} image={fileUrl(p.image)} type="product" />
      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <nav className="flex items-center gap-1.5 text-xs text-stone mb-8">
          <Link to="/boutique" className="hover:text-brand">Boutique</Link><ChevronRight size={13} /><span className="text-forest font-medium">{p.name}</span>
        </nav>
        <div className="grid md:grid-cols-2 gap-10">
          <div className="rounded-[24px] overflow-hidden bg-sage aspect-square relative" data-testid="product-image">
            {p.image && <img src={fileUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />}
            {p.on_sale && <span className="absolute top-4 left-4 rounded-full bg-terracotta text-cream text-sm font-bold px-4 py-1.5 flex items-center gap-1"><Tag size={14} /> Offre du moment</span>}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-terracotta">{p.category}</span>
              {p.sku && <span className="text-xs text-stone">Réf. {p.sku}</span>}
            </div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl text-forest leading-tight flex items-start justify-between gap-3">
              <span>{p.name}</span>
              <FavoriteButton productId={p.id} className="h-11 w-11 border border-border shrink-0 mt-1" />
            </h1>
            <p className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {p.group_price != null ? (
                <>
                  <span className="font-display text-3xl text-brand">{p.group_price.toFixed(2)} €</span>
                  <span className="text-lg text-stone line-through">{Number(p.list_price ?? p.price).toFixed(2)} €</span>
                  <span className="text-xs font-bold bg-brand/10 text-brand rounded-full px-2.5 py-1" data-testid="pd-group-badge">-{p.group_discount_percent}%{p.group_name ? ` · ${p.group_name}` : ""}</span>
                </>
              ) : (
                <>
                  <span className={`font-display text-3xl ${p.on_sale ? "text-terracotta" : "text-brand"}`}>{p.effective_price.toFixed(2)} €</span>
                  {p.on_sale && <span className="text-lg text-stone line-through">{p.price.toFixed(2)} €</span>}
                </>
              )}
              {isMat && <span className="text-sm text-stone">/ {p.unit_label}</span>}
            </p>
            <p className="mt-5 text-stone leading-relaxed">{p.description}</p>
            <p className={`mt-4 text-sm font-medium ${p.stock > 0 ? "text-green-600" : "text-red-500"}`}>{p.stock > 0 ? `En stock (${p.stock} disponible${p.stock > 1 ? "s" : ""})` : "Rupture de stock"}</p>

            {(p.depot_stock || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="depot-availability">
                {p.depot_stock.map((ds, i) => (
                  <span key={i} className={`text-xs font-medium rounded-full px-3 py-1 flex items-center gap-1.5 ${ds.in_stock ? "bg-green-100 text-green-700" : "bg-stone/15 text-stone"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${ds.in_stock ? "bg-green-500" : "bg-stone/50"}`} />
                    {ds.in_stock ? `En stock à ${ds.name}` : `Sur commande à ${ds.name}`}
                  </span>
                ))}
              </div>
            )}

            {isMat && (Number(p.density) > 0 || Number(p.footprint_m2) > 0) && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="material-specs">
                {Number(p.density) > 0 && <span className="text-xs font-medium rounded-full bg-forest/10 text-forest px-3 py-1">Densité : {Number(p.density)}</span>}
                {Number(p.footprint_m2) > 0 && <span className="text-xs font-medium rounded-full bg-forest/10 text-forest px-3 py-1">Emprise au sol : {Number(p.footprint_m2)} m²</span>}
              </div>
            )}

            {isMat && (p.sold_by === "m3" || p.sold_by === "bigbag") ? <VolumeCalc p={p} add={add} /> : p.stock > 0 && (
              <div className="mt-6 flex items-center gap-4">
                <div className="flex items-center rounded-full border border-border">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 flex items-center justify-center text-forest" data-testid="qty-minus"><Minus size={16} /></button>
                  <span className="w-8 text-center font-medium" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="h-11 w-11 flex items-center justify-center text-forest" data-testid="qty-plus"><Plus size={16} /></button>
                </div>
                <button onClick={() => { add(p, qty); toast.success("Ajouté au panier"); }} data-testid="add-to-cart"
                  className="flex-1 rounded-full bg-brand text-cream py-3.5 font-semibold hover:bg-forest transition-colors flex items-center justify-center gap-2"><ShoppingCart size={18} /> Ajouter au panier</button>
              </div>
            )}
            {!isMat && <button onClick={() => { add(p, qty); nav("/boutique/panier"); }} disabled={p.stock <= 0} data-testid="buy-now" className="mt-3 w-full rounded-full border border-brand text-brand py-3 font-semibold hover:bg-brand/5 disabled:opacity-40">Commander maintenant</button>}

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 rounded-xl bg-white border border-border p-3"><Store size={18} className="text-brand" /> Retrait à Saint-Victoret</div>
              <div className="flex items-center gap-2 rounded-xl bg-white border border-border p-3"><Truck size={18} className="text-brand" /> Livraison possible{p.delivery_delay ? ` · ${p.delivery_delay}` : ""}</div>
            </div>

            <ServiceQuote p={p} />
          </div>
        </div>

        {/* Plant care */}
        {isVeg && (p.planting_period || p.watering || p.sun_exposure) && (
          <div className="mt-14" data-testid="plant-care">
            <h2 className="font-display text-3xl text-forest mb-5 flex items-center gap-2"><Sprout size={24} className="text-brand" /> Conseils de culture</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <CareItem icon={Sprout} label="Période de plantation" value={p.planting_period} />
              <CareItem icon={Droplets} label="Arrosage" value={p.watering} />
              <CareItem icon={Sun} label="Exposition" value={p.sun_exposure} />
            </div>
            {p.care_tips && <p className="mt-4 text-sm text-stone italic">💡 {p.care_tips}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
